import { ComboField } from '~/components/form';
import { Button } from '~/components/ui/button';
import { PAGE_SIZE_OPTIONS, showPager } from '~/lib/listParams';

export type PageSlot = number | 'gap-start' | 'gap-end';

/** The pager's page slots, 1-based: always at most 7, so the row never grows or jumps as the page changes. */
export function pageWindow(current: number, total: number): PageSlot[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, 'gap-end', total];
  if (current >= total - 3)
    return [1, 'gap-start', total - 4, total - 3, total - 2, total - 1, total];
  return [1, 'gap-start', current - 1, current, current + 1, 'gap-end', total];
}

const NAV_CLASS =
  'flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-800 dark:bg-white/[0.03]';

interface PaginationProps {
  /** Zero-based current page. */
  pageIndex: number;
  pageSize: number;
  pageCount: number;
  totalRows: number;
  onPageChange: (pageIndex: number) => void;
  /** Omit when the server owns the page size and the caller has nowhere to send a change. */
  pageSizeControl?: { value: number; onChange: (size: number) => void; options?: number[] };
}

export function Pagination({
  pageIndex,
  pageSize,
  pageCount,
  totalRows,
  onPageChange,
  pageSizeControl,
}: PaginationProps) {
  // One page of rows needs no pager (D-26), including a client-paged bounded snapshot.
  if (!showPager(totalRows, pageSize)) return null;

  const current = pageIndex + 1;
  const from = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, totalRows);

  return (
    <nav aria-label="Pagination" className={NAV_CLASS}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {pageSizeControl && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 dark:text-gray-400">Show</span>
            <ComboField
              id="datatable-page-size"
              testId="datatable-page-size"
              ariaLabel="Rows per page"
              className="w-24"
              compact
              searchable
              value={String(pageSizeControl.value)}
              options={(pageSizeControl.options ?? PAGE_SIZE_OPTIONS).map((size) => ({
                value: String(size),
                label: String(size),
              }))}
              onChange={(value) => pageSizeControl.onChange(Number(value))}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">entries</span>
          </div>
        )}
        <p className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
          Showing {from} to {to} of {totalRows} entries
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <Button
          variant="outline"
          onClick={() => onPageChange(pageIndex - 1)}
          disabled={pageIndex <= 0}
        >
          Previous
        </Button>
        {pageWindow(current, pageCount).map((slot) =>
          typeof slot === 'string' ? (
            <span
              key={slot}
              aria-hidden="true"
              className="flex size-9 items-center justify-center text-sm text-gray-500 dark:text-gray-400"
            >
              …
            </span>
          ) : (
            <Button
              key={slot}
              size="icon"
              variant={slot === current ? 'primary' : 'outline'}
              ariaLabel={`Page ${slot}`}
              ariaCurrent={slot === current ? 'page' : undefined}
              onClick={() => onPageChange(slot - 1)}
            >
              {slot}
            </Button>
          ),
        )}
        <Button
          variant="outline"
          onClick={() => onPageChange(pageIndex + 1)}
          disabled={pageIndex >= pageCount - 1}
        >
          Next
        </Button>
      </div>
    </nav>
  );
}

interface HasMorePaginationProps {
  /** Zero-based current page. */
  pageIndex: number;
  /** The server said another page follows. */
  hasMore: boolean;
  onPageChange: (pageIndex: number) => void;
}

/** The pager for a server list that returns `hasMore` and no total (D-26 cannot apply): the page number and Previous / Next, only once there is a second page. */
export function HasMorePagination({ pageIndex, hasMore, onPageChange }: HasMorePaginationProps) {
  if (pageIndex <= 0 && !hasMore) return null;

  return (
    <nav aria-label="Pagination" className={NAV_CLASS}>
      <p className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
        Page {pageIndex + 1}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          onClick={() => onPageChange(pageIndex - 1)}
          disabled={pageIndex <= 0}
        >
          Previous
        </Button>
        <Button variant="outline" onClick={() => onPageChange(pageIndex + 1)} disabled={!hasMore}>
          Next
        </Button>
      </div>
    </nav>
  );
}
